import { Component, OnInit } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { DeveloperClientsService } from 'src/app/services/developer-clients.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { DeveloperClientFormComponent } from '../developer-client-form/developer-client-form.component';
import { SecretDisplayDialogComponent } from '../secret-display-dialog/secret-display-dialog.component';
import { scopeLabelKey } from '../oauth-scope-labels';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-developer-clients-list',
  templateUrl: './developer-clients-list.component.html',
  styleUrls: ['./developer-clients-list.component.scss']
})
export class DeveloperClientsListComponent implements OnInit {
  clients: any[] = [];
  page: number = 1;
  count: number = 10;
  total: number = 0;
  scopeLabelKey = scopeLabelKey;

  constructor(
    private devClientsService: DeveloperClientsService,
    private dialogService: NbDialogService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadClients();
  }

  loadClients(): void {
    this.spinner.show();
    this.devClientsService.getClients().subscribe({
      next: (res: any) => {
        this.clients = Array.isArray(res) ? res : res.response?.data || [];
        this.total = this.clients.length;
        this.spinner.hide();
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire(
          'Error',
          err.error?.response?.message || this.translate.instant('DEV_CLIENTS.LOAD_ERROR'),
          'error'
        );
      }
    });
  }

  openCreateDialog(): void {
    this.dialogService.open(DeveloperClientFormComponent).onClose.subscribe(result => {
      if (result?.saved) {
        this.loadClients();
      }
    });
  }

  openEditDialog(client: any): void {
    this.dialogService
      .open(DeveloperClientFormComponent, { context: { clientId: client.clientId } })
      .onClose.subscribe(result => {
        if (result?.saved) {
          this.loadClients();
        }
      });
  }

  rotateSecret(client: any): void {
    Swal.fire({
      title: this.translate.instant('DEV_CLIENTS.ROTATE.TITLE', { name: client.name }),
      text: this.translate.instant('DEV_CLIENTS.ROTATE.TEXT'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: this.translate.instant('DEV_CLIENTS.ROTATE.CONFIRM'),
      cancelButtonText: this.translate.instant('DEV_CLIENTS.FORM.CANCEL'),
    }).then(result => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.devClientsService.rotateSecret(client.clientId).subscribe({
          next: (res: any) => {
            this.spinner.hide();
            const body = res.response?.data || res;
            this.dialogService.open(SecretDisplayDialogComponent, {
              context: {
                clientId: body.clientId || client.clientId,
                clientSecret: body.client_secret,
                titleKey: 'DEV_CLIENTS.SECRET_DIALOG.ROTATED_TITLE',
              }
            });
            this.loadClients();
          },
          error: (err: any) => {
            this.spinner.hide();
            Swal.fire(
              'Error',
              err.error?.response?.message || this.translate.instant('DEV_CLIENTS.ROTATE.ERROR'),
              'error'
            );
          }
        });
      }
    });
  }

  revokeClient(client: any): void {
    Swal.fire({
      title: this.translate.instant('DEV_CLIENTS.REVOKE.TITLE', { name: client.name }),
      text: this.translate.instant('DEV_CLIENTS.REVOKE.TEXT'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: this.translate.instant('DEV_CLIENTS.REVOKE.CONFIRM'),
      cancelButtonText: this.translate.instant('DEV_CLIENTS.FORM.CANCEL'),
    }).then(result => {
      if (result.isConfirmed) {
        this.spinner.show();
        this.devClientsService.revokeClient(client.clientId).subscribe({
          next: () => {
            this.spinner.hide();
            this.toastr.success(this.translate.instant('DEV_CLIENTS.REVOKE.SUCCESS'));
            this.loadClients();
          },
          error: (err: any) => {
            this.spinner.hide();
            Swal.fire(
              'Error',
              err.error?.response?.message || this.translate.instant('DEV_CLIENTS.REVOKE.ERROR'),
              'error'
            );
          }
        });
      }
    });
  }

  activateClient(client: any): void {
    this.spinner.show();
    this.devClientsService.activateClient(client.clientId).subscribe({
      next: () => {
        this.spinner.hide();
        this.toastr.success(this.translate.instant('DEV_CLIENTS.ACTIVATE.SUCCESS'));
        this.loadClients();
      },
      error: (err: any) => {
        this.spinner.hide();
        Swal.fire(
          'Error',
          err.error?.response?.message || this.translate.instant('DEV_CLIENTS.ACTIVATE.ERROR'),
          'error'
        );
      }
    });
  }

  pageChanged(event: number): void {
    this.page = event;
  }

  formatLastUsed(date: string): string {
    if (!date) return this.translate.instant('DEV_CLIENTS.NEVER_USED');
    return new Date(date).toLocaleString();
  }

  formatCreated(date: string): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  }

  formatScopes(scopes: string[]): string[] {
    return scopes || [];
  }

  accessLabel(client: any): string {
    if (client.chainId && typeof client.chainId === 'object') {
      const chainLabel = client.chainId.chainName || client.chainId.name;
      if (chainLabel) {
        return chainLabel;
      }
    }
    if (client.chainId && typeof client.chainId === 'string') {
      return client.chainId;
    }
    const fromStoreIds = (client.storeIds || [])
      .map((s: any) => s.name || s.storeName)
      .filter(Boolean);
    if (fromStoreIds.length) {
      return fromStoreIds.join(', ');
    }
    if (client.storeId?.name || client.storeId?.storeName) {
      return client.storeId.name || client.storeId.storeName;
    }
    return '-';
  }
}
